import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import { KButton, KInput, KSpan, KTextArea } from "kahuna-base-react-components"

import { assistant } from "../../../apis"
import { errorFilledToastOptions } from "../../../constants"
import { getLanguageJsonFor } from "../../../constants/languages"
import {
  CoManagerAttachment,
  CoManagerCatalog,
  CoManagerCatalogRelease,
  CoManagerCatalogTrack,
  CoManagerInfringementScan
} from "../../../types"
import { formatIsoDate, mapToCamelCase } from "../../../utility"

// What may go on a report. The same list the support requests take, because a rights document is a
// PDF or a photograph of one, and the server holds the same list.
const DOCUMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "application/pdf"
]
const DOCUMENT_TYPE_BY_EXTENSION: { [extension: string]: string } = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  pdf: "application/pdf"
}
const documentType = (file: File) =>
  file.type || DOCUMENT_TYPE_BY_EXTENSION[(file.name.split(".").pop() || "").toLowerCase()] || ""
const MAX_DOCUMENTS = 5
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024
// How many releases the picker shows before the artist has typed anything. A catalog is browsed at the
// top and searched below that, and a list of three hundred rows inside a chat bubble is neither.
const VISIBLE_RELEASES = 6

const fill = (template: string, values: { [key: string]: string }) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

/**
 * The conversation's own strings. Handed in rather than read off the interface language, because the
 * assistant answers in the artist's language and this card sits inside that answer.
 */
type CoManagerWords = ReturnType<typeof getLanguageJsonFor>["co_manager"]

/**
 * The report an artist files when somebody else has put their music out.
 *
 * Four steps, and each one exists because the step before it cannot be skipped. Which recording,
 * because a takedown is filed on an ISRC and a UPC and nothing else identifies a record. Then the scan
 * of the three platforms Kahuna can see by those codes, which is what turns "somebody stole my song"
 * into an address somebody can act on. Then the links for everywhere Kahuna cannot look, which is most
 * of the internet. Then the document, because no platform lifts a release on an assertion.
 *
 * Its own component rather than another method on the chat: it holds nine pieces of state that belong
 * to one card, and one report is one of these on screen at a time.
 */
const InfringementCard: FC<{
  words: CoManagerWords
  onSent: () => void
}> = ({ words, onSent }) => {
  const [catalog, setCatalog] = useState<CoManagerCatalog | null>(null)
  const [catalogFailed, setCatalogFailed] = useState(false)
  const [search, setSearch] = useState("")
  const [release, setRelease] = useState<CoManagerCatalogRelease | null>(null)
  const [track, setTrack] = useState<CoManagerCatalogTrack | null>(null)
  const [scan, setScan] = useState<CoManagerInfringementScan | null>(null)
  const [scanning, setScanning] = useState(false)
  const [scanFailed, setScanFailed] = useState(false)
  // The uses the artist has ruled out. Held as what is dismissed rather than what is kept, so a scan
  // result arrives with everything already reported and they only touch the ones that are theirs.
  const [dismissed, setDismissed] = useState<string[]>([])
  const [extraLinks, setExtraLinks] = useState("")
  const [applicantName, setApplicantName] = useState("")
  const [note, setNote] = useState("")
  const [documents, setDocuments] = useState<CoManagerAttachment[]>([])
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const documentInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    assistant("/co-manager/catalog/", "get")
      .then((response) => setCatalog(mapToCamelCase<CoManagerCatalog>(response.data)))
      .catch(() => setCatalogFailed(true))
  }, [])

  const releases = useMemo(() => {
    const all = catalog?.releases || []
    const query = search.trim().toLocaleLowerCase()
    if (!query) return all.slice(0, VISIBLE_RELEASES)

    // The codes are searched as well as the titles: an artist who has the ISRC in front of them,
    // because a platform's form asked for it, should be able to paste it here.
    return all
      .filter(
        (item) =>
          item.title.toLocaleLowerCase().includes(query) ||
          (item.upc || "").includes(query) ||
          item.tracks.some(
            (candidate) =>
              candidate.title.toLocaleLowerCase().includes(query) ||
              (candidate.isrc || "").toLocaleLowerCase().includes(query)
          )
      )
      .slice(0, VISIBLE_RELEASES)
  }, [catalog, search])

  /** Runs the scan for one recording. Slow on purpose: it is three platforms, live. */
  const runScan = (picked: CoManagerCatalogRelease) => {
    setScanning(true)
    setScanFailed(false)
    assistant("/co-manager/claim-scan/", "POST", { release_id: picked.id }, {}, false)
      .then((response) => setScan(mapToCamelCase<CoManagerInfringementScan>(response.data)))
      .catch(() => setScanFailed(true))
      .finally(() => setScanning(false))
  }

  const pickRelease = (picked: CoManagerCatalogRelease) => {
    setRelease(picked)
    // One track needs no question, and the scan is by the release's codes either way: the track is
    // asked for so the report carries an ISRC, not to decide what gets scanned.
    setTrack(picked.tracks.length === 1 ? picked.tracks[0] : null)
    setDismissed([])
    setScan(null)
    runScan(picked)
  }

  const startOver = () => {
    setRelease(null)
    setTrack(null)
    setScan(null)
    setScanFailed(false)
    setDismissed([])
    setSearch("")
  }

  const readDocument = (file: File) =>
    new Promise<CoManagerAttachment>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = String(reader.result).replace(/^data:[^;,]*;?base64,/, `data:${documentType(file)};base64,`)
        resolve({ name: file.name, dataUrl })
      }
      reader.onerror = () => reject(new Error(file.name))
      reader.readAsDataURL(file)
    })

  const attachDocuments = async (files?: FileList | File[] | null) => {
    const picked = Array.from(files || [])
    if (!picked.length) return

    const room = MAX_DOCUMENTS - documents.length
    if (room <= 0) {
      toast.error(fill(words.claim_file_count_error, { count: String(MAX_DOCUMENTS) }), errorFilledToastOptions)
      return
    }

    const usable = picked.filter((file) => {
      if (!DOCUMENT_TYPES.includes(documentType(file))) {
        toast.error(words.claim_file_type_error, errorFilledToastOptions)
        return false
      }
      if (file.size > MAX_DOCUMENT_BYTES) {
        toast.error(words.claim_file_size_error, errorFilledToastOptions)
        return false
      }
      return true
    })

    if (usable.length > room) {
      toast.error(fill(words.claim_file_count_error, { count: String(MAX_DOCUMENTS) }), errorFilledToastOptions)
    }

    const read = await Promise.all(usable.slice(0, room).map((file) => readDocument(file).catch(() => null)))
    const done = read.filter((file): file is CoManagerAttachment => !!file)

    if (done.length < usable.slice(0, room).length) {
      toast.error(words.claim_file_read_error, errorFilledToastOptions)
    }
    if (done.length) {
      setDocuments((previous) => [...previous, ...done].slice(0, MAX_DOCUMENTS))
    }
  }

  // What the report will carry: the scan results still ticked, plus whatever the artist pasted in.
  const links = useMemo(() => {
    const found = (scan?.platforms || [])
      .flatMap((platform) => platform.uses.map((use) => use.url))
      .filter((url) => !dismissed.includes(url))
    const typed = extraLinks
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)

    return Array.from(new Set([...found, ...typed]))
  }, [scan, dismissed, extraLinks])

  const send = async () => {
    if (!links.length) {
      toast.error(words.infringement_links_required, errorFilledToastOptions)
      return
    }
    if (!documents.length) {
      toast.error(words.infringement_documents_required, errorFilledToastOptions)
      return
    }

    setSending(true)
    try {
      await assistant(
        "/co-manager/claim-log/",
        "POST",
        {
          release_id: release?.id,
          track_id: track?.id,
          links,
          note: note.trim(),
          applicant_name: applicantName.trim(),
          // The check this claim came out of, so the row the rights team opens carries the scan behind
          // it: which platforms held the codes, and every id found there.
          takedown_log_id: scan?.takedownLogId,
          files: documents.map((file) => ({ name: file.name, data_url: file.dataUrl }))
        },
        {},
        // Not abortable: the requests share a controller per path, and this one must not be cancelled
        // by whatever the artist types next.
        false
      )
      setSent(true)
      onSent()
    } catch (error: any) {
      const invalid = error?.response?.data?.link
      toast.error(
        invalid ? fill(words.infringement_link_invalid, { link: invalid }) : words.infringement_send_error,
        errorFilledToastOptions
      )
    } finally {
      setSending(false)
    }
  }

  const label = (text: string) => <KSpan text={text} fontSize={11} lineHeight="15px" color="#888" />

  const renderPicker = () => (
    <>
      {label(words.infringement_pick)}
      <KInput
        value={search}
        placeholder={words.infringement_search}
        padding="10px"
        onChange={(value) => setSearch(value)}
      />
      <div className="co-manager-picker">
        {releases.map((item) => (
          <KButton
            key={`release-${item.id}`}
            text={`${item.title}${item.releaseDate ? ` · ${formatIsoDate(item.releaseDate)}` : ""}`}
            onClick={() => pickRelease(item)}
            background="#FFF"
            hoverBackground="#FAFAFA"
            border="1px solid #EDEDED"
            hoverBorder="1px solid #D6D6D6"
            borderRadius={8}
            textColor="#111"
            fontSize={13}
            fontWeight={400}
            width="100%"
            height="auto"
            padding="8px 10px"
            shadowDisabled
          />
        ))}
        {!releases.length && label(catalog ? words.infringement_empty : words.infringement_scanning)}
      </div>
      {catalog?.truncated && !search.trim() && label(words.infringement_truncated)}
    </>
  )

  const renderTracks = () => (
    <>
      {label(words.infringement_tracks)}
      <div className="co-manager-picker">
        {(release?.tracks || []).map((item) => (
          <KButton
            key={`track-${item.id}`}
            text={`${item.title}${item.isrc ? ` · ${item.isrc}` : ""}`}
            onClick={() => setTrack(item)}
            background={track?.id === item.id ? "#111" : "#FFF"}
            hoverBackground={track?.id === item.id ? "#333" : "#FAFAFA"}
            border={track?.id === item.id ? "1px solid #111" : "1px solid #EDEDED"}
            hoverBorder={track?.id === item.id ? "1px solid #333" : "1px solid #D6D6D6"}
            borderRadius={8}
            textColor={track?.id === item.id ? "#FFF" : "#111"}
            fontSize={13}
            fontWeight={400}
            width="100%"
            height="auto"
            padding="8px 10px"
            shadowDisabled
          />
        ))}
      </div>
    </>
  )

  const renderScan = () => {
    if (scanning) {
      return (
        <div className="co-manager-scan-status">
          <img src="/account_icons/loading-3-dots.svg" style={{ width: 24 }} alt="" />
          {label(words.infringement_scanning)}
        </div>
      )
    }

    if (scanFailed) return label(words.infringement_scan_error)
    if (!scan) return null

    return (
      <>
        {label(scan.foundCount ? words.infringement_found : words.infringement_none)}
        {scan.platforms
          .filter((platform) => platform.uses.length || !platform.checked)
          .map((platform) => (
            <div className="co-manager-scan-platform" key={`platform-${platform.platform}`}>
              <KSpan text={platform.label} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
              {!platform.checked && label(words.infringement_platform_failed)}
              {platform.uses.map((use) => {
                const ruledOut = dismissed.includes(use.url)
                return (
                  <div className="co-manager-scan-use" key={use.url}>
                    <KButton
                      dataTestId="co-manager-toggle-use"
                      // Ticked means reported, which is how the scan hands them over: the artist only
                      // touches the ones that turn out to be their own.
                      icon={ruledOut ? "/account_icons/close-line.svg" : "/catalog_icons/check.svg"}
                      onClick={() =>
                        setDismissed((previous) =>
                          ruledOut ? previous.filter((url) => url !== use.url) : [...previous, use.url]
                        )
                      }
                      background="transparent"
                      hoverBackground="#EFEFEF"
                      borderRadius={100}
                      width="24px"
                      height="24px"
                      padding="5px"
                      shadowDisabled
                    />
                    <div className="co-manager-scan-use-text">
                      <KSpan
                        text={use.title || use.url}
                        fontSize={12}
                        lineHeight="16px"
                        color={ruledOut ? "#B2B2B2" : "#111"}
                        ellipsis
                      />
                      {/* Only when there is one. The scan keeps the platform's own ids rather than
                          titles, so most rows are a link and nothing else, and repeating the link
                          underneath itself says nothing. */}
                      {!!use.owner && (
                        <KSpan text={use.owner} fontSize={11} lineHeight="15px" color="#999" ellipsis />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        {!!scan.unchecked.length && label(fill(words.infringement_unchecked, { platforms: scan.unchecked.join(", ") }))}
      </>
    )
  }

  const renderForm = () => (
    <>
      {label(words.infringement_other)}
      <KTextArea
        value={extraLinks}
        placeholder={words.infringement_links_placeholder}
        padding="10px"
        onChange={(value) => setExtraLinks(value)}
        disabled={sending}
        height={72}
      />
      <KInput
        value={applicantName}
        placeholder={words.infringement_applicant}
        padding="10px"
        onChange={(value) => setApplicantName(value)}
        disabled={sending}
      />
      <KTextArea
        value={note}
        placeholder={words.infringement_note}
        padding="10px"
        onChange={(value) => setNote(value)}
        disabled={sending}
        height={56}
      />
      {!!documents.length && (
        <div className="co-manager-claim-documents">
          {documents.map((file, index) => (
            <div className="co-manager-claim-document" key={`document-${index}-${file.name}`}>
              <KSpan text={file.name} fontSize={12} lineHeight="16px" color="#111" />
              {!sending && (
                <KButton
                  dataTestId="co-manager-remove-report-document"
                  icon="/account_icons/close-line.svg"
                  onClick={() => setDocuments((previous) => previous.filter((_, at) => at !== index))}
                  background="transparent"
                  hoverBackground="#EFEFEF"
                  borderRadius={100}
                  shadowDisabled
                  width="20px"
                  height="20px"
                  padding="4px"
                />
              )}
            </div>
          ))}
        </div>
      )}
      {label(words.infringement_documents)}
      <input
        ref={documentInputRef}
        type="file"
        accept={DOCUMENT_TYPES.join(",")}
        className="hidden"
        multiple
        onChange={(event) => {
          attachDocuments(event.target.files)
          // Cleared so picking the same file twice in a row still fires a change.
          event.target.value = ""
        }}
      />
      <div className="co-manager-bubble-actions">
        <KButton
          dataTestId="co-manager-attach-report-document"
          text={words.infringement_attach}
          leftIcon="/account_icons/attachment-line.svg"
          onClick={() => documentInputRef.current?.click()}
          disabled={sending || documents.length >= MAX_DOCUMENTS}
          background="#FFF"
          hoverBackground="#FAFAFA"
          border="1px solid #E6E6E6"
          hoverBorder="1px solid #D6D6D6"
          textColor="#111"
          width="auto"
          height="34px"
          padding="7px 14px"
          gap="6px"
          borderRadius={100}
          fontSize={14}
          shadowDisabled
        />
        <KButton
          dataTestId="co-manager-send-infringement-report"
          text={sending ? words.infringement_sending : words.infringement_send}
          onClick={send}
          disabled={sending || !links.length || !documents.length}
          background="#111"
          textColor="#FFF"
          width="auto"
          height="34px"
          padding="7px 16px"
          borderRadius={100}
          fontSize={14}
        />
      </div>
    </>
  )

  return (
    <div className="co-manager-claim-form">
      <KSpan text={words.infringement_title} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
      {catalogFailed && label(words.infringement_catalog_error)}
      {sent ? (
        <KSpan text={words.infringement_sent} fontSize={12} lineHeight="16px" color="#666" />
      ) : (
        <>
          {!release && !catalogFailed && renderPicker()}
          {release && (
            <>
              <div className="co-manager-scan-header">
                <KSpan
                  text={`${release.title}${release.upc ? ` · ${release.upc}` : ""}`}
                  fontSize={12}
                  lineHeight="16px"
                  fontWeight={500}
                  color="#111"
                  ellipsis
                />
                <KButton
                  dataTestId="co-manager-change-recording"
                  text={words.infringement_change}
                  onClick={startOver}
                  disabled={scanning || sending}
                  background="transparent"
                  shadowDisabled
                  width="auto"
                  height="auto"
                  padding="0px"
                  fontSize={11}
                  textColor="#6B7280"
                />
              </div>
              {release.tracks.length > 1 && renderTracks()}
              {renderScan()}
              {!scanning && renderForm()}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default InfringementCard
